// 予約・コースの API (#1138): 予約CRUD/変更/公開share/slots/コースCRUD。
// 配置ルールは index.ts のヘッダ参照。
import { API_BASE, ApiError, apiFetch, trackedFetch } from './_client'

/** #1504 店舗都合キャンセル前の持ち出しプレビュー (GET /reservations/:id/store-cancel-preview) */
export interface StoreCancelPreview {
  /** capture 済み (= 返金しても決済手数料が戻らない) か。false なら与信のみで手数料ゼロ */
  captured: boolean
  capturedAmount: number
  paymentTiming: 'prepaid' | 'deposit' | 'onsite' | null
  /** Stripe 決済手数料 + 税 (店舗負担)。balance_transaction 未確定なら null */
  stripeFee: number | null
  /** YOKOSO PAY 手数料 (返金時に店舗へ戻る) */
  platformFee: number | null
}

/** 返し方: 対面 = cash / オンライン = paypay・paypal (お客様の請求リンク) ・bank_transfer (お店で引換券を
 * 読み取ったうえで)。'other' は #2308 ② でやめた (古い記録の表示だけ) */
export type RefundAlternateMethod = 'cash' | 'bank_transfer' | 'paypay' | 'paypal' | 'other'
/** お店の引き換え (引換券を読み取って) で選べる返し方 */
export type RefundRedeemMethod = 'cash' | 'bank_transfer'
/** #2308 ③ bank_transfer = お客様が口座を入れ、お店が振り込む (日本の方向け。期限なし) */
export type RefundOnlineKind = 'paypay' | 'paypal' | 'bank_transfer'

/** #2308 ③ R9 銀行口座 (お客様の画面では口座番号が下 3 桁だけ) */
export interface RefundBankAccount {
  bankName: string
  branchName: string
  accountType: 'ordinary' | 'current'
  accountNumber: string
  holderName: string
}

/** #2308 ② オンラインの請求の状態 (services/pay/api/domains/shop/usecase/payment/refund_online.py の view) */
export interface RefundOnline {
  /** requested = お店の支払い待ち / paid = お店が払った (14 日の窓) / claimed = 「届いていない」/ expired = リンク切れ (作り直し待ち) */
  state: 'requested' | 'paid' | 'claimed' | 'expired' | null
  kind: RefundOnlineKind | null
  url?: string | null
  bank?: RefundBankAccount | null
  /** #2308 ③ R10 そのお店が使える方法 (銀行振込は常に)。引換券が issued のときだけ入る */
  availableKinds?: RefundOnlineKind[]
  /** #2308 ③ R11 期日切れで、お客様にチャージバック (カード会社への申し立て) を案内した */
  disputeAdvised?: boolean
  submittedAt?: string | null
  payableFrom: string | null
  deadlineAt?: string | null
  expiresAt?: string | null
  recreateCount?: number
  paid?: { at: string | null; ref: string | null } | null
  claimUntil?: string | null
  claim?: { at: string | null } | null
  /** 期限切れで、切れたリンクが上限に達した (運営への連絡を案内する。決めるのはサーバ) */
  contactOps?: boolean
}

/** #2226 お客様の返金の引換券 (本人だけ) */
export interface RefundVoucher {
  /** #2308 ② オンラインの請求の操作に使う */
  id: string
  code: string
  qr: string
  amount: number
  /** paid = オンラインでお店が払った (14 日の窓が閉じたら redeemed) */
  status: 'issued' | 'paid' | 'redeemed' | 'void'
  redeemedAt: string | null
  method: RefundAlternateMethod | null
  online: RefundOnline | null
}

/** #2226 お店の画面の引換券 (コードは返らない) */
export interface StoreRefundVoucher {
  id: string
  // void = 額が変わったなどで出し直した古い引換券 (引き換えられない)
  status: 'issued' | 'paid' | 'redeemed' | 'void'
  amount: number
  issuedAt: string | null
  redeemedAt: string | null
  redeemedBy: string | null
  method: RefundAlternateMethod | null
  // 予約の引換券は reservation、体験のチケットの引換券 (#2298) は ticket (どちらか片方)
  reservation: { id: string; shortCode?: string; date?: string; time?: string; partySize?: number; contactName?: string } | null
  ticket?: { id: string; productName?: string; slotDate?: string; slotStartTime?: string; quantity?: number } | null
  // #2308 販売 (取引の履歴からの返金) の引換券
  sale?: { id: string; amount?: number; createdAt?: string | null; refundedAt?: string | null; refundAmount?: number } | null
  // #2308 ② オンラインの請求の状態 (無ければ null)
  online?: RefundOnline | null
}

export const reservationsApi = {
  // Reservations
  createReservation: async (payload: {
    storeId: string; date: string; time: string; partySize: number;
    contactName: string; contactEmail?: string; contactPhone?: string; notes?: string;
    courseId?: string;   // #737
    /** #1644 飲み放題 = 実体プラン (#1353) の id。course.drinkPlanIds 掲載分のみ可、
     * 名称・価格はサーバが実体プランから引く */
    drinkPlanId?: string;
    /** #1644 画面に表示した単価のエコー。店主の同時価格編集とズレた時に 409 で止まる */
    drinkPlanPriceYen?: number;
    /** #2190 申し込みの鍵 (lib/reservationRequestKey)。同じ鍵ならサーバは同じ予約を返す */
    requestKey?: string;
  }) => {
    const res = await apiFetch('/reservations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return res.json()
  },

  getMyReservations: async () => {
    const res = await apiFetch('/reservations/me')
    return res.json()
  },

  // #1024 (F1b): 予約の Firestore 直読を廃止し、店舗側の予約一覧はこの API に一本化。
  // owner|manager 対応（apiFetch が staff/owner トークンを自動添付）。
  getStoreReservations: async (storeId: string, status?: string) => {
    const qs = status ? `?storeId=${storeId}&status=${status}` : `?storeId=${storeId}`
    const res = await apiFetch(`/reservations${qs}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load reservations')
    return res.json()
  },

  updateReservationStatus: async (
    reservationId: string,
    // #738 B-1: visited / no_show を追加。店主が当日承認後に押す。
    status: 'approved' | 'rejected' | 'cancelled' | 'visited' | 'no_show',
    // #1348 tableNumber: visited 時にコース予約を伝票へ起票する際の卓割り当て
    // message: 却下時にお客様へ伝える一言 (変更の却下なら「この日なら空いています」等)
    options: { reasonCode?: string; closeSlot?: boolean; tableNumber?: string; idToken?: string; message?: string } = {},
  ) => {
    const { reasonCode, closeSlot, tableNumber, idToken, message } = options
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`
    const body: Record<string, unknown> = { status }
    if (reasonCode !== undefined) body.reasonCode = reasonCode
    if (closeSlot !== undefined) body.closeSlot = closeSlot
    if (tableNumber) body.tableNumber = tableNumber
    if (message && message.trim()) body.message = message.trim()
    const init: RequestInit = {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    }
    const res = idToken
      ? await trackedFetch(`${API_BASE}/reservations/${reservationId}`, init)
      : await apiFetch(`/reservations/${reservationId}`, init)
    if (!res.ok) throw new ApiError(res.status, 'Failed to update reservation')
    return res.json()
  },

  // #1375: 失敗した返金の再実行。失敗のまま放置すると「客に返していない金を
  // 店が持っている」状態が見えないまま残る。自動再試行にしないのは、店舗アカウントの
  // 残高不足など**同じ理由で落ち続ける**ことが多く、状況を確認した人が押す方が確実なため。
  // #2226 返金の引換券 (返金の記録 規則 6)。カードに返せなかった返金を、お店が別の方法で返す。
  // お客様: 自分の予約の引換券 (QR とコード。本人だけ)
  getMyRefundVoucher: async (reservationId: string) => {
    const res = await apiFetch(`/reservations/${reservationId}/refund-voucher`)
    return res.json() as Promise<RefundVoucher>
  },
  // お店 (owner|manager): 引き換えの前の確認。本物で、未引き換えか
  lookupRefundVoucher: async (code: string) => {
    const res = await apiFetch(`/reservations/refund-vouchers/${encodeURIComponent(code)}`)
    return res.json() as Promise<StoreRefundVoucher>
  },
  // お店 (owner|manager): 引き換える。**お金を渡すのはこれが通った後**。2 回目は 409
  redeemRefundVoucher: async (code: string, method: RefundRedeemMethod, note?: string) => {
    const res = await apiFetch(`/reservations/refund-vouchers/${encodeURIComponent(code)}/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method, note: note || null }),
    })
    return res.json() as Promise<StoreRefundVoucher>
  },
  // #2308 ② オンラインで返す (お客様の PayPay / PayPal の請求リンクにお店が払う)。決まりは
  // services/pay/api/domains/shop/usecase/payment/refund_online.py。お客様: 自分の請求リンクを入れる (差し替えも)。本人だけ
  submitRefundOnlineLink: async (voucherId: string, url: string) => {
    const res = await apiFetch(`/reservations/refund-vouchers/${encodeURIComponent(voucherId)}/online`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
    return res.json() as Promise<RefundVoucher>
  },
  // #2308 ③ お客様: 銀行口座を入れる (差し替えも)。日本の口座だけ。本人だけ
  submitRefundBankAccount: async (voucherId: string, account: RefundBankAccount) => {
    const res = await apiFetch(`/reservations/refund-vouchers/${encodeURIComponent(voucherId)}/online/bank`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(account),
    })
    return res.json() as Promise<RefundVoucher>
  },
  // お客様: 「届いていない」(お店が払ってから 14 日以内)
  claimRefundNotReceived: async (voucherId: string, note?: string) => {
    const res = await apiFetch(`/reservations/refund-vouchers/${encodeURIComponent(voucherId)}/online/not-received`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note || null }),
    })
    return res.json() as Promise<RefundVoucher>
  },
  // お店 (owner|manager): オンラインの請求の一覧
  listOnlineRefunds: async (storeId: string) => {
    const res = await apiFetch(`/reservations/refund-vouchers/online/${encodeURIComponent(storeId)}`)
    return res.json() as Promise<{ items: StoreRefundVoucher[] }>
  },
  // お店 (owner|manager): 「払いました」(取引の番号は必須)
  markOnlineRefundPaid: async (voucherId: string, ref: string) => {
    const res = await apiFetch(`/reservations/refund-vouchers/${encodeURIComponent(voucherId)}/online/paid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref }),
    })
    return res.json() as Promise<StoreRefundVoucher>
  },

  retryReservationRefund: async (reservationId: string) => {
    const res = await apiFetch(`/reservations/${reservationId}/retry-refund`, {
      method: 'POST',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to retry refund')
    return res.json() as Promise<{
      refundStatus: string
      refundedAmount: number
      refundError: string | null
    }>
  },

  // #1386 その日の時間帯ごとの空き席。電話を受けながら「19 時は空いてますか？」に
  // 答えるための表示に使う。
  getDayOccupancy: async (storeId: string, date: string) => {
    const params = new URLSearchParams({ storeId, date })
    const res = await apiFetch(`/reservations/day-occupancy?${params}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load occupancy')
    return res.json() as Promise<{
      reservableSeats: number | null
      slots: { time: string; used: number; remaining: number | null }[]
    }>
  },

  // #1386 電話等で受けた予約を店舗側から登録する。
  //
  // **アプリ外の予約を載せないと空き枠が実態とずれる。** 電話予約が入っていても
  // アプリは空いて見え、二重予約になる。
  //
  // 席数上限を超えると 409 `over_capacity` を返す。店が「詰めればいける」と
  // 判断したら `force: true` で再送する（禁止すると紙管理に戻ってしまう）。
  createStoreReservation: async (payload: {
    storeId: string
    date: string
    time: string
    partySize: number
    contactName: string
    contactPhone?: string
    notes?: string
    courseId?: string
    force?: boolean
  }) => {
    const res = await apiFetch('/reservations/store-entry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      // 呼び出し側が `over_capacity` を見て「それでも登録する」を出せるよう
      // code を拾って渡す。detail が無い形の応答もあるので握り潰す。
      let code: string | undefined
      try { code = (await res.json())?.detail?.code } catch { /* 本文なし */ }
      throw new ApiError(res.status, 'Failed to create reservation', code)
    }
    return res.json()
  },

  // #1340: 店舗都合キャンセル (承認後も可)。顧客キャンセル (PATCH status=cancelled) とは
  // 別 endpoint — 認可が店舗 principal で、精算も無条件 (与信解放 + 全額返金) になる。
  storeCancelReservation: async (
    reservationId: string,
    options: { reasonCode: string; reasonText?: string; closeSlot?: boolean },
  ) => {
    const res = await apiFetch(`/reservations/${reservationId}/store-cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to cancel reservation')
    return res.json()
  },

  // #2577 カードの成功の直後に、予約の決済をサーバに確かめてもらう (webhook を待たずに与信済みにする)
  confirmReservationPayment: async (reservationId: string) => {
    const res = await apiFetch(`/reservations/${reservationId}/payment/confirm`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to confirm reservation payment')
    return res.json() as Promise<{
      status: string; paymentStatus: string | null; paymentIntentId: string | null; holdExpired?: boolean
    }>
  },

  // #738 A-2 / C-1: 予約成立後に Stripe PaymentIntent を作成して clientSecret を取得
  createReservationPaymentIntent: async (reservationId: string) => {
    const res = await apiFetch(`/reservations/${reservationId}/payment-intent`, {
      method: 'POST',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to create payment intent')
    return res.json() as Promise<{
      clientSecret: string
      paymentIntentId: string
      connectedAccountId: string
      amount: number
      captureMethod: 'automatic' | 'manual'
    }>
  },

  // #1804 予約の前払い / 手付を YOKOSO ポイントで払う。
  // カードと違って与信が無いのでその場で引く。**予約は既に作られている**ので、
  // 成功したらそのまま完了 (Stripe の確認画面に相当する段が無い)。
  reservationPointsCheckout: async (
    reservationId: string,
    topup?: { topupAmount: number; topupFeeJpyc: number },
  ) => {
    const res = await apiFetch(`/reservations/${reservationId}/points-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(topup || {}),
    })
    return res.json() as Promise<{
      reservationId: string
      transactionId: string
      amount: number
      newBalance: number
      campaignReward: number | null
    }>
  },

  // #745 Phase 2.1: 顧客が予約人数を変更する。
  // - 減少: 部分返金 (refundAmount を返す)
  // - 増加: 増分の新規 PI を返す (= client が決済 UI 起動)、status は 'pending' に戻る
  changeReservationPartySize: async (
    reservationId: string,
    partySize: number,
    reason?: string,
  ) => {
    const res = await apiFetch(`/reservations/${reservationId}/party-size`, {
      method: 'PATCH',
      body: JSON.stringify({ partySize, reason: reason || undefined }),
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to change party size')
    }
    return res.json() as Promise<{
      id: string
      partySize: number
      status: string
      totalAmount?: number | null
      refundAmount?: number  // 減少時のみ
      incrementalPaymentIntent?: {
        clientSecret: string
        paymentIntentId: string
        amount: number
        captureMethod: 'automatic' | 'manual'
        connectedAccountId: string
      } | null  // 増加 + course 予約時のみ
    }>
  },

  // #745 Phase 2.2: 顧客が予約日時を変更する。
  // status は 'pending' に戻り店主再承認待ち。決済 (PaymentIntent) は持ち越し。
  // 店舗側の日時変更 (owner|manager)。お客様から電話で頼まれた日程変更を店が直接入れる。
  // コース・人数は変えない (決済額が変わらない) ので日程のみ。再承認は不要
  // #1504 店舗都合キャンセルの前に「いくら持ち出しになるか」を実額で出す。
  // 承認 (capture) 済みなら Stripe の balance_transaction から決済手数料が確定している。
  // 与信のみなら手数料は発生しない (解放するだけ)。
  storeCancelPreview: async (reservationId: string): Promise<StoreCancelPreview> => {
    const res = await apiFetch(`/reservations/${reservationId}/store-cancel-preview`)
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to load cancel preview')
    }
    return res.json()
  },

  storeChangeReservationDatetime: async (
    reservationId: string,
    options: { date: string; time: string; note?: string },
  ) => {
    const res = await apiFetch(`/reservations/${reservationId}/store-datetime`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...options, note: options.note?.trim() || undefined }),
    })
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to change datetime')
    }
    return res.json()
  },

  changeReservationDatetime: async (
    reservationId: string,
    date: string,
    time: string,
    reason?: string,
  ) => {
    const res = await apiFetch(`/reservations/${reservationId}/datetime`, {
      method: 'PATCH',
      body: JSON.stringify({ date, time, reason: reason || undefined }),
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to change datetime')
    }
    return res.json() as Promise<{ id: string; date: string; time: string; status: string }>
  },

  // #745 Phase 2.3: 顧客が予約コースを変更する。同 store 内の別 active コースへ切替。
  // 価格差分: 減額 → 部分返金 / 増額 → 増分 PI。status は 'pending' に戻る。
  changeReservationCourse: async (
    reservationId: string,
    courseId: string,
    reason?: string,
  ) => {
    const res = await apiFetch(`/reservations/${reservationId}/course`, {
      method: 'PATCH',
      body: JSON.stringify({ courseId, reason: reason || undefined }),
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to change course')
    }
    return res.json() as Promise<{
      id: string
      courseId: string
      courseName: string | null
      totalAmount: number
      status: string
      refundAmount?: number
      incrementalPaymentIntent?: {
        clientSecret: string
        paymentIntentId: string
        amount: number
        captureMethod: 'automatic' | 'manual'
        connectedAccountId: string
      } | null
    }>
  },

  // #746 旧予約 (= shortCode 未付与) に対する lazy backfill。
  // shortCode 既存ならそれを返す (idempotent)。client が「共有」「詳細を見る」
  // を押した時にだけ呼ぶ。
  ensureReservationShortCode: async (reservationId: string) => {
    const res = await apiFetch(`/reservations/${reservationId}/short-code`, {
      method: 'POST',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to ensure short code')
    return res.json() as Promise<{ shortCode: string }>
  },

  // #746 公開 share URL `/r/{shortCode}` lookup — 未ログインで PII 除外版が見える
  getPublicReservation: async (shortCode: string) => {
    const res = await trackedFetch(`${API_BASE}/r/${encodeURIComponent(shortCode)}`)
    if (res.status === 404) throw new ApiError(404, 'not_found')
    if (res.status === 410) throw new ApiError(410, 'expired')
    if (!res.ok) throw new ApiError(res.status, 'Failed to load shared reservation')
    return res.json() as Promise<{
      shortCode: string
      status: string
      date: string
      time: string
      partySize: number
      courseName: string | null
      totalAmount: number | null
      paymentTiming: 'prepaid' | 'deposit' | 'onsite' | null
      store: {
        id: string
        name: string | null
        address: string | null
        googleMapsUrl: string | null
        lat: number | null
        lng: number | null
        phoneNumber: string | null
      }
    }>
  },

  // #1386 partySize を渡すと席数で絞り込まれる。**渡さないと 1 名扱い**になり、
  // 「4 名では入れない枠」が空いて見える。人数が決まっているなら必ず渡すこと。
  getReservationSlots: async (
    storeId: string,
    date: string,
    courseId?: string,
    partySize?: number | null,
  ) => {
    const params = new URLSearchParams({ storeId, date })
    if (courseId) params.set('courseId', courseId)
    if (partySize) params.set('partySize', String(partySize))
    const res = await trackedFetch(`${API_BASE}/reservations/slots?${params}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load slots')
    return res.json()
  },

  // #737 Courses
  listCoursesByStore: async (storeId: string, includeInactive = false) => {
    const qs = includeInactive ? '?include_inactive=true' : ''
    const res = await trackedFetch(`${API_BASE}/courses/store/${storeId}${qs}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load courses')
    return res.json() as Promise<{ courses: import('../../store/useStore').Course[] }>
  },

  getCourse: async (storeId: string, courseId: string) => {
    const res = await trackedFetch(`${API_BASE}/courses/store/${storeId}/${courseId}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load course')
    return res.json() as Promise<import('../../store/useStore').Course>
  },

  createCourse: async (storeId: string, payload: Omit<import('../../store/useStore').Course, 'id' | 'storeId' | 'createdAt' | 'updatedAt'>) => {
    const res = await apiFetch(`/courses/store/${storeId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to create course')
    return res.json() as Promise<import('../../store/useStore').Course>
  },

  updateCourse: async (storeId: string, courseId: string, updates: Partial<Omit<import('../../store/useStore').Course, 'id' | 'storeId' | 'createdAt' | 'updatedAt'>>) => {
    const res = await apiFetch(`/courses/store/${storeId}/${courseId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update course')
    return res.json() as Promise<import('../../store/useStore').Course>
  },

  deleteCourse: async (storeId: string, courseId: string) => {
    const res = await apiFetch(`/courses/store/${storeId}/${courseId}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete course')
    return res.json()
  },

  // #1353 放題プラン（飲み放題 / 食べ放題）
  listAllYouCanPlans: async (storeId: string, includeInactive = false) => {
    const qs = includeInactive ? '?include_inactive=true' : ''
    const res = await trackedFetch(`${API_BASE}/all-you-can/store/${storeId}${qs}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load plans')
    return res.json() as Promise<{ plans: import('../../store/useStore').AllYouCanPlan[] }>
  },

  createAllYouCanPlan: async (
    storeId: string,
    payload: Omit<import('../../store/useStore').AllYouCanPlan, 'id'>,
  ) => {
    const res = await apiFetch(`/all-you-can/store/${storeId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to create plan')
    return res.json() as Promise<import('../../store/useStore').AllYouCanPlan>
  },

  updateAllYouCanPlan: async (
    storeId: string,
    planId: string,
    // `clearWeekendPrice`: 週末の基本の単価を外す (#2052。null は未指定と区別が付かないため旗で送る)
    updates: Partial<Omit<import('../../store/useStore').AllYouCanPlan, 'id'>>
      & { clearWeekendPrice?: boolean },
  ) => {
    const res = await apiFetch(`/all-you-can/store/${storeId}/${planId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update plan')
    return res.json() as Promise<import('../../store/useStore').AllYouCanPlan>
  },

  deleteAllYouCanPlan: async (storeId: string, planId: string) => {
    const res = await apiFetch(`/all-you-can/store/${storeId}/${planId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete plan')
    return res.json()
  },

  /** 卓の放題プラン適用状態（残り時間 / ラストオーダー込み） */
  getTableAllYouCan: async (storeId: string, tableNumber: string) => {
    const res = await trackedFetch(`${API_BASE}/all-you-can/store/${storeId}/table/${tableNumber}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load table plan')
    return res.json() as Promise<{
      tableNumber: string
      allYouCan: import('../../store/useStore').AllYouCanState | null
    }>
  },

  /** 卓にプランを付ける。**この瞬間が時間管理の起点**（店員は時刻を打たない） */
  applyAllYouCanToTable: async (
    storeId: string,
    // #2039 freePartySize = 時間制で料金を取らない人数 (人数の内数)。アプリは性別を見ず、
    // 誰が無料かは店員が決めて人数で入れる
    payload: { planId: string; tableNumber: string; partySize?: number; freePartySize?: number },
  ) => {
    const res = await apiFetch(`/all-you-can/store/${storeId}/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to apply plan')
    return res.json()
  },

  /** 開始 / 終了時刻の手動調整（遅延・延長）と人数の訂正。
   *  partySize を渡すと**卓の適用人数と伝票金額を一緒に**打ち直し、訂正後の
   *  伝票合計を `billTotal` で返す（時間は動かさない）。 */
  adjustTableAllYouCan: async (
    storeId: string,
    payload: {
      tableNumber: string
      extendMinutes?: number
      startedAt?: string
      partySize?: number
      freePartySize?: number
    },
  ): Promise<{ tableNumber: string; allYouCan: import('../../store/useStore').AllYouCanState | null; billTotal?: number | null }> => {
    const res = await apiFetch(`/all-you-can/store/${storeId}/adjust`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to adjust plan')
    return res.json()
  },

  clearTableAllYouCan: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch(`/all-you-can/store/${storeId}/table/${tableNumber}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to clear plan')
    return res.json()
  },
}
